import * as React from 'react'
import { ParkedSaleRow } from './ParkedSaleRow'
import { ParkedSaleStore } from './ParkedSaleStore'
import { observer, inject } from 'mobx-react'
import moment from 'moment'
import { Table, Tag, Popover } from 'antd'
import {
  IPaymentQueueWithExternalSource,
  PaymentQueueOrderType,
  PaymentQueueStatus,
} from '@getgreenline/homi-shared'
import { abbreviateText, capitalizeFirstLetter } from '../../../../utilities/helpers'
import { Link } from 'react-router'
import { Routes } from '../../../../constants/Routes'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { padStart } from 'lodash'
import ParkedSaleStatusButton from './ParkedSaleStatusButton'
import { ParkedSaleButtonStatus } from './ParkedSaleStatusButtonLists'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props {
  parkedSaleStore: ParkedSaleStore
}

interface ParkedSaleTableRecord {
  key: number
  externalSourceName: JSX.Element
  externalOrderId: string | number | null
  orderType: JSX.Element
  customerName: string | null
  notes: JSX.Element | string | null
  createDate: JSX.Element | string
  paymentLines: JSX.Element[]
  status: JSX.Element
}

@inject('currentCompanyStore')
@observer
export class ParkedSaleTable extends React.Component<Props & InjectedProps> {
  dashboardSalesLink = (paymentQueue: IPaymentQueueWithExternalSource) => {
    const { company } = this.props.currentCompanyStore!

    return (
      paymentQueue.completedPayment && (
        <Link
          to={Routes.DASHBOARD_SALES(company!.id, paymentQueue.completedPayment!.id)}
          target='_blank'
        >
          {paymentQueue.completedPayment!.customId}
        </Link>
      )
    )
  }

  getExternalOrderId = (externalOrderId: number | null) => {
    return externalOrderId && externalOrderId < 1000
      ? padStart(`${externalOrderId}`, 4, '0')
      : externalOrderId
  }

  getOrder = (paymentQueue: IPaymentQueueWithExternalSource) => {
    return (
      <>
        <Tag color={paymentQueue.externalSourceHexColor || 'green'}>
          {paymentQueue.externalSourceName || BLAZE_COMPANY}
        </Tag>
        {paymentQueue.completedPayment && (
          <div className='parked-sale-tag-paid' onClick={(e) => e.stopPropagation()}>
            <Tag color='blue'>Paid</Tag> {this.dashboardSalesLink(paymentQueue)}
          </div>
        )}
      </>
    )
  }

  getOrderType = (paymentQueue: IPaymentQueueWithExternalSource) => {
    const { orderType } = paymentQueue
    return (
      <span
        style={
          paymentQueue.orderType === PaymentQueueOrderType.PICKUP
            ? { fontWeight: 400, color: 'var(--gray)' }
            : { fontWeight: 600, color: 'var(--dark)' }
        }
      >
        {capitalizeFirstLetter(orderType)}
      </span>
    )
  }

  getNotes = (paymentQueue: IPaymentQueueWithExternalSource) => {
    return paymentQueue.notes && abbreviateText(paymentQueue.notes, 12).isAbbr ? (
      <Popover
        className='parked-sales-notes'
        content={<div onClick={(e) => e.stopPropagation()}>{paymentQueue.notes}</div>}
        title='Notes'
        placement='top'
        trigger='hover'
      >
        {abbreviateText(paymentQueue.notes, 12).text}
        <p>See more</p>
      </Popover>
    ) : (
      paymentQueue.notes
    )
  }

  getCreateDate = (paymentQueue: IPaymentQueueWithExternalSource) => {
    const { compareOneHourFromNow } = this.props.parkedSaleStore
    const { createDate, status } = paymentQueue
    const isOverADayOld = moment().diff(createDate, 'days') >= 1
    const isExcludedStatus =
      status === PaymentQueueStatus.COMPLETED || status === PaymentQueueStatus.CANCELLED
    const shouldShowWarning = isOverADayOld && !isExcludedStatus

    if (compareOneHourFromNow(createDate)) {
      return (
        <>
          <p className={shouldShowWarning ? 'text-danger' : ''}>
            {moment(createDate).format('MMM D, h:mm a')}
          </p>
          ({moment(createDate).fromNow()})
        </>
      )
    } else {
      return (
        <p className={shouldShowWarning ? 'text-danger' : ''}>
          {moment(createDate).format('MMM D, h:mm a')}
        </p>
      )
    }
  }

  getPaymentLines = (paymentQueue: IPaymentQueueWithExternalSource) => {
    const { paymentLines } = paymentQueue

    return paymentLines.map((paymentLine, index) => {
      return (
        <div key={index} className='payment-line'>
          <p>
            {paymentLine.productName}
            {paymentLine.sku && (
              <span className='text-muted sku-number'>SKU: {paymentLine.sku}</span>
            )}
          </p>
          <p className='text-right'>x {paymentLine.quantity}</p>
        </div>
      )
    })
  }

  getButtonStatus = (status: PaymentQueueStatus | undefined) => {
    switch (status) {
      case PaymentQueueStatus.PENDING:
      case PaymentQueueStatus.READY_FOR_PICKUP:
      case PaymentQueueStatus.READY_FOR_DELIVERY:
      case PaymentQueueStatus.OUT_FOR_DELIVERY:
        return ParkedSaleButtonStatus.ACTIVE
        break
      case PaymentQueueStatus.CANCELLED:
      case PaymentQueueStatus.COMPLETED:
        return ParkedSaleButtonStatus.DISABLED
        break
      default:
        return ParkedSaleButtonStatus.DEFAULT
    }
  }

  getStatus = (paymentQueue: IPaymentQueueWithExternalSource) => {
    const { status } = paymentQueue

    return <ParkedSaleStatusButton status={status} buttonStatus={this.getButtonStatus(status)} />
  }

  getPaymentQueueRow = (paymentQueue: IPaymentQueueWithExternalSource) => {
    const { id, externalOrderId, customerName } = paymentQueue

    return {
      key: id,
      externalSourceName: this.getOrder(paymentQueue),
      externalOrderId: this.getExternalOrderId(externalOrderId),
      orderType: this.getOrderType(paymentQueue),
      customerName: abbreviateText(customerName || '', 8).text,
      notes: this.getNotes(paymentQueue),
      createDate: this.getCreateDate(paymentQueue),
      paymentLines: this.getPaymentLines(paymentQueue),
      status: this.getStatus(paymentQueue),
    }
  }

  get tableColumns() {
    return [
      { title: 'Order', dataIndex: 'externalSourceName', key: 'externalSourceName' },
      { title: 'ID', dataIndex: 'externalOrderId', key: 'externalOrderId' },
      { title: 'Order type', dataIndex: 'orderType', key: 'orderType' },
      { title: 'Customer', dataIndex: 'customerName', key: 'customerName' },
      { title: 'Note', dataIndex: 'notes', key: 'notes' },
      { title: 'Created', dataIndex: 'createDate', key: 'createDate' },
      { title: 'Products', dataIndex: 'paymentLines', key: 'paymentLines' },
      { title: 'Status', dataIndex: 'status', key: 'status' },
    ]
  }

  get tableDataSource(): ParkedSaleTableRecord[] {
    const { parkedSaleStore } = this.props
    const { paymentQueues } = parkedSaleStore
    return paymentQueues.map((pq) => this.getPaymentQueueRow(pq))
  }

  render() {
    const { parkedSaleStore } = this.props
    const { paymentQueues } = parkedSaleStore

    return (
      <div className='table-responsive'>
        <Table<ParkedSaleTableRecord>
          columns={this.tableColumns}
          dataSource={this.tableDataSource}
          pagination={false}
          rowClassName={() => 'cursor-pointer'}
          onRow={(record) => ({
            onClick: () => parkedSaleStore.setToggleParkedSaleModal(true, record.key),
          })}
        />
      </div>
    )
  }
}
